import type { DemoResponse } from './types';

// ─── Demo responses ──────────────────────────────────────────────────────────

const DEMO_RESPONSES: Record<string, DemoResponse> = {
  default: {
    answer:
      'Ich konnte keine spezifischen Informationen zu dieser Anfrage finden. ' +
      'Bitte versuche eine genauere Formulierung oder wähle eine der vorgeschlagenen Fragen.',
    nodes: [],
    edges: [],
  },

  schalung: {
    answer:
      'Die Wandschalung Framax Xlife wird in **4 Schritten** montiert:\n\n' +
      '1. Grundrahmen positionieren und ausrichten\n' +
      '2. Ankersystem setzen\n' +
      '3. Schalungsplatten einhängen\n' +
      '4. Abspannung und Sicherung\n\n' +
      'Achte auf den **Mindestabstand von 15 cm** zur Bewehrung.',
    nodes: [
      {
        id: 'q',
        label: 'Wandschalung montieren',
        type: 'question',
        score: 1.0,
      },
      {
        id: 'd1',
        label: 'Montageanleitung\nFramax Xlife v3.2',
        type: 'document',
        score: 0.97,
        source: 'Handbuch_Framax.pdf',
        detail:
          'Kapitel 4.2 – Schritt-für-Schritt Montageanleitung mit Sicherheitshinweisen für die Wandschalung.',
      },
      {
        id: 'd1b',
        label: 'Technisches Merkblatt\nAnkerstab RM20',
        type: 'document',
        score: 0.91,
        source: 'Merkblatt_RM20.pdf',
        detail:
          'Technische Daten und Anzugsmomente für Ankerstab RM20 inkl. Korrosionsschutzhinweise.',
      },
      {
        id: 'd1c',
        label: 'Betriebsanweisung\nSchalungsreinigung',
        type: 'document',
        score: 0.85,
        source: 'BA_Reinigung_v2.pdf',
        detail:
          'Reinigungsintervalle, zugelassene Trennmittel und Entsorgungsvorschriften nach DGUV.',
      },
      {
        id: 'd2',
        label: 'CAD Schnittzeichnung\nXlife Ankersystem',
        type: 'image',
        score: 0.89,
        source: 'CAD_Xlife_Anker.dwg',
        detail:
          'Technische Zeichnung des Ankersystems mit Maßangaben und Materialliste.',
      },
      {
        id: 'd3',
        label: 'Dipl.-Ing. K. Müller\nSchalungsexperte',
        type: 'expert',
        score: 0.82,
        source: 'Interview 2025-11-12',
        detail:
          'Senior Schalung & Gerüste – 18 Jahre Erfahrung. Hat Montageleitfaden für interne Schulungen verfasst.',
      },
      {
        id: 'd3b',
        label: 'Ing. M. Weber\nBaustellenleiter',
        type: 'expert',
        score: 0.79,
        source: 'Interview 2025-11-18',
        detail:
          'Verantwortlich für Schalungsplanung auf Großbaustellen. Spezialist für Deckenschalung und Sondergeometrien.',
      },
      {
        id: 'd4',
        label: 'Norm DIN 18202\nMaßtoleranzen im Bau',
        type: 'norm',
        score: 0.75,
        source: 'DIN 18202:2019',
        detail:
          'Grenzabmaße für Bauleistungen. Maßgebend für Schaltoleranzen und Ausrichtgenauigkeit.',
      },
    ],
    edges: [
      { from: 'q', to: 'd1' },
      { from: 'q', to: 'd1b' },
      { from: 'q', to: 'd1c' },
      { from: 'q', to: 'd2' },
      { from: 'q', to: 'd3' },
      { from: 'q', to: 'd3b' },
      { from: 'q', to: 'd4' },
    ],
  },

  sicherheit: {
    answer:
      'Für Gerüstarbeiten **ab 2 m Höhe** ist persönliche Schutzausrüstung (PSA) Pflicht:\n\n' +
      '- Sicherheitsgeschirr\n' +
      '- Helm nach **EN 397**\n' +
      '- Sicherheitsschuhe **S3**\n\n' +
      'Das Gerüst muss täglich durch eine befähigte Person geprüft werden. ' +
      'Maximale Windgeschwindigkeit für den Aufbau: **12 m/s**.',
    nodes: [
      {
        id: 'q',
        label: 'Sicherheitsvorschriften\nGerüstarbeiten',
        type: 'question',
        score: 1.0,
      },
      {
        id: 'd1',
        label: 'DGUV Regel 101-011\nGerüstbau',
        type: 'norm',
        score: 0.98,
        source: 'DGUV 101-011',
        detail:
          'Berufsgenossenschaftliche Regel für Sicherheit und Gesundheitsschutz beim Gerüstbau.',
      },
      {
        id: 'd2',
        label: 'PSA-Checkliste\nHöhenarbeiten 2024',
        type: 'document',
        score: 0.93,
        source: 'PSA_Checklist_2024.pdf',
        detail:
          'Interne Checkliste für persönliche Schutzausrüstung bei Arbeiten über 2 m.',
      },
      {
        id: 'd3',
        label: 'Ing. S. Berger\nArbeitssicherheit',
        type: 'expert',
        score: 0.86,
        source: 'Interview 2025-09-03',
        detail: 'Sicherheitsbeauftragte – zuständig für Unterweisungen und Gerüstprüfungen.',
      },
    ],
    edges: [
      { from: 'q', to: 'd1' },
      { from: 'q', to: 'd2' },
      { from: 'q', to: 'd3' },
    ],
  },

  geruest: {
    answer:
      '**Systemgerüst** und **Standgerüst** unterscheiden sich in Aufbau und Einsatzbereich:\n\n' +
      '| Merkmal | Systemgerüst | Standgerüst |\n' +
      '|---------|-------------|-------------|\n' +
      '| Aufbau | Modulares Stecksystem | Rohr & Kupplung |\n' +
      '| Flexibilität | Eingeschränkt | Sehr hoch |\n' +
      '| Aufbauzeit | Kurz | Länger |\n' +
      '| Typische Anwendung | Fassadenarbeiten | Sondergerüste |\n\n' +
      'Systemgerüste sind schneller aufzubauen und erlauben standardisierte Prüfprozesse.',
    nodes: [
      {
        id: 'q',
        label: 'Systemgerüst vs.\nStandgerüst',
        type: 'question',
        score: 1.0,
      },
      {
        id: 'd1',
        label: 'Gerüstbaukatalog\nSYSTECS 2024',
        type: 'document',
        score: 0.94,
        source: 'Gerauestbaukatalog_2024.pdf',
        detail: 'Vollständiger Katalog aller Gerüsttypen mit Einsatzempfehlungen und Lastangaben.',
      },
      {
        id: 'd2',
        label: 'DIN EN 12811\nGerüstbaunorm',
        type: 'norm',
        score: 0.91,
        source: 'DIN EN 12811:2004',
        detail: 'Europäische Norm für Arbeits- und Schutzgerüste. Definiert Anforderungen an Tragfähigkeit.',
      },
      {
        id: 'd3',
        label: 'Dipl.-Ing. H. Schulz\nGerüstbaumeister',
        type: 'expert',
        score: 0.78,
        source: 'Interview 2025-08-21',
        detail: 'Gerüstbaumeister mit 22 Jahren Erfahrung im Industriegerüstbau.',
      },
    ],
    edges: [
      { from: 'q', to: 'd1' },
      { from: 'q', to: 'd2' },
      { from: 'q', to: 'd3' },
    ],
  },
};

// ─── Demo mode detection ─────────────────────────────────────────────────────

const RETRIEVAL_HEALTH_URL = '/api/python/retrieval/health';

/**
 * Returns true when the live retrieval backend is unavailable or not ready.
 * Performs a lightweight health-check against the Python API.
 * Results are cached for 30 s to avoid hammering the endpoint.
 */
let _demoModeCache: { value: boolean; ts: number } | null = null;

export async function isDemoMode(): Promise<boolean> {
  const now = Date.now();
  if (_demoModeCache && now - _demoModeCache.ts < 30_000) {
    return _demoModeCache.value;
  }
  try {
    const res = await fetch(RETRIEVAL_HEALTH_URL, { method: 'GET' });
    if (!res.ok) throw new Error('not ok');
    const data = (await res.json()) as { status?: string };
    const alive = data.status === 'ready' || data.status === 'partial';
    _demoModeCache = { value: !alive, ts: now };
    return !alive;
  } catch {
    _demoModeCache = { value: true, ts: now };
    return true;
  }
}

// ─── Keyword matcher ─────────────────────────────────────────────────────────

/** Returns the demo response that best matches the question via keyword matching. */
export function matchDemoResponse(question: string): DemoResponse {
  const q = question.toLowerCase();

  if (
    q.includes('schalung') ||
    q.includes('montier') ||
    q.includes('framax') ||
    q.includes('wand') ||
    q.includes('anker')
  ) {
    return DEMO_RESPONSES.schalung;
  }

  if (
    q.includes('sicherheit') ||
    q.includes('psa') ||
    q.includes('schutz') ||
    q.includes('unfal') ||
    q.includes('höhe')
  ) {
    return DEMO_RESPONSES.sicherheit;
  }

  if (
    q.includes('gerüst') ||
    q.includes('system') ||
    q.includes('stand') ||
    q.includes('unterschied') ||
    q.includes('vergleich')
  ) {
    return DEMO_RESPONSES.geruest;
  }

  return DEMO_RESPONSES.default;
}
